import React from 'react';
import graphql from 'babel-plugin-relay/macro';
import { useMutation } from 'react-relay/hooks';
import {
  useGlobalUser,
  useLoginModal,
  useNoAccountModal,
  useSegment,
} from '@sparknz/journeys-common';
import {
  Button,
  ButtonVariant,
  useSnackbar,
} from '@sparknz/spark-pattern-library';
import { updateTrackedUser } from '@sparknz/tracking';

import {
  ChangePlanButtonMutation,
  ChangePlanButtonMutation$data,
} from '__generated__/ChangePlanButtonMutation.graphql';
import {
  EXISTING_PLAN_ERR_MSG,
  GENERIC_ERR_MSG,
} from 'components/shop/MobilePlans/constants/changePlan.constants';
import { useChangePlanDetails } from 'components/shop/MobilePlans/providers';
import { ChangePlanActionTypes } from 'components/shop/MobilePlans/reducer/changePlan.reducer';
import {
  trackChangePlanEvent,
  trackModalWindowViewEvent,
} from 'components/shop/MobilePlans/utils/tracking.utils';

interface ChangePlanButtonProps {
  label: string;
  planId: string;
  variant: ButtonVariant;
}

interface HandleChangePlanRedirectProps {
  accounts: ChangePlanButtonMutation$data['updateMobileChangePlanUser']['accounts'];
  eligibleLines: ChangePlanButtonMutation$data['updateMobileChangePlanUser']['eligibleLines'];
  redirectPath: string;
}

// TODO: remove enableAccountSelector from updateMobileChangePlanUser args in BFF
const changePlanMutation = graphql`
  mutation ChangePlanButtonMutation($channel: CartChannel!, $offerId: String!) {
    updateMobileChangePlanUser(
      channel: $channel
      offerId: $offerId
      enableAccountSelector: true
    ) {
      redirectPath
      shouldShowErrorSnackbar
      shouldOpenNoAccountModal
      errorReason
      errorMessage
      eligibleLines {
        accountNumber
        accountName
        lineNumber
        offerId
        offerName
      }
      accounts {
        accountNumber
        firstName
        lastName
      }
    }
  }
`;

export default function ChangePlanButton({
  label,
  planId,
  variant,
}: ChangePlanButtonProps) {
  const { openLoginModal } = useLoginModal();
  const {
    authenticatedInfo: { isLoggedIn, hasSparkId },
    refetchUser,
  } = useGlobalUser();
  const { showSnackbar } = useSnackbar();
  const { channel } = useSegment();
  const { openNoAccountModal } = useNoAccountModal();
  const { dispatch } = useChangePlanDetails();

  const [commitChangePlan, isInFlight] =
    useMutation<ChangePlanButtonMutation>(changePlanMutation);

  const handleChangePlanRedirect = ({
    accounts,
    eligibleLines,
    redirectPath,
  }: HandleChangePlanRedirectProps) => {
    if (accounts) {
      if (accounts?.length > 1) {
        dispatch({
          type: ChangePlanActionTypes.SetManageAccounts,
          payload: {
            accounts,
            eligibleLines,
            redirect: {
              path: redirectPath,
              planId,
            },
          },
        });
      } else if (eligibleLines && eligibleLines.length > 1) {
        dispatch({
          type: ChangePlanActionTypes.SetManageLines,
          payload: {
            accounts,
            eligibleLines,
            redirect: {
              path: redirectPath,
              planId,
            },
            selectedAccount: accounts[0].accountNumber,
          },
        });
      }
    } else if (eligibleLines && eligibleLines[0].offerId === planId) {
      showSnackbar({
        text: EXISTING_PLAN_ERR_MSG,
        variant: 'error',
      });
    } else {
      window.location.assign(redirectPath);
    }
  };

  const getChangePlanData = () => {
    return commitChangePlan({
      variables: {
        channel,
        offerId: planId,
      },
      onCompleted(data) {
        const {
          redirectPath,
          shouldShowErrorSnackbar,
          shouldOpenNoAccountModal,
          errorMessage,
          eligibleLines,
          accounts,
        } = data.updateMobileChangePlanUser;
        if (shouldShowErrorSnackbar) {
          if (errorMessage) {
            showSnackbar({
              text: errorMessage,
              variant: 'error',
              buttonText: 'Message us',
              onClick: () => window.location.assign('/shop/message-us/'),
            });
          } else {
            showSnackbar({
              text: GENERIC_ERR_MSG,
              variant: 'error',
            });
          }

          return;
        }

        if (shouldOpenNoAccountModal) {
          trackModalWindowViewEvent(
            'Shop Account Selection Error Modal Window',
          );

          openNoAccountModal({
            onContinue: () => {
              return Promise.all([refetchUser, getChangePlanData]);
            },
            hideCheckout: true,
          });

          return;
        }

        if (redirectPath) {
          handleChangePlanRedirect({ accounts, eligibleLines, redirectPath });
          return;
        }

        showSnackbar({
          text: GENERIC_ERR_MSG,
          variant: 'error',
        });
      },
      onError() {
        showSnackbar({
          text: GENERIC_ERR_MSG,
          variant: 'error',
        });
      },
    });
  };

  function handleLoginSuccess(): void {
    updateTrackedUser();
    refetchUser();
    getChangePlanData();
  }

  function handleClick() {
    trackChangePlanEvent();
    if (!isLoggedIn || !hasSparkId) {
      openLoginModal({
        onLoginSuccess: handleLoginSuccess,
      });
    } else {
      getChangePlanData();
    }
  }

  return (
    <Button variant={variant} onClick={handleClick} isLoading={isInFlight}>
      {label}
    </Button>
  );
}
